/**
 * useRasterTilesLoading — AK-383 #8.
 *
 * Reports whether a MapLibre raster source is currently fetching tiles, so the
 * UI can show an immediate spinner after the user changes the satellite date
 * (or product). Without this feedback a date switch feels «завис / кнопка не
 * сработала» — the COG tiles take a beat to arrive and nothing on screen moves
 * (CLAUDE.md UX §3: no «толкай и жди»).
 *
 * How it works
 * ------------
 * MapLibre doesn't expose a first-class «is this source loading» boolean that
 * survives a `setTiles()` swap cleanly, so we derive it from events:
 *   - a change in `loadToken` (the tile URL / date the caller is loading) flips
 *     us to `true` synchronously — instant feedback on the click, before any
 *     network round-trip;
 *   - `sourcedata` for our source with `isSourceLoaded === true`, or a global
 *     `idle` event (all tiles for the current viewport loaded + rendered),
 *     flips us back to `false`;
 *   - `error` on the source also clears the spinner (we don't hang forever on a
 *     failed tile — the raster-overlay effect handles the actual error).
 *
 * `loadToken === null` (no active raster / no date) means «nothing to load» →
 * never loading.
 *
 * Teardown-safe: every listener is removed on cleanup and re-gated through
 * `isMapAlive` so a callback still in flight after `map.remove()` can't touch a
 * dead style (grabля #394). No store writes, no imperative style churn — pure
 * observation, safe to run alongside the raster-source effect.
 *
 * Reusable across surfaces: /map (SatellitesPanel / SatelliteStrip date
 * selector, source `ndvi-active-raster`) and the field card mini-map
 * (FieldOverviewTab, source `field-ndvi-raster`). Caller passes the map ref,
 * the source id, and the current load token.
 *
 * WP4 M4 (2026-08-24): this hook already knows the exact start/stop moments
 * of a raster switch — it's the natural place to measure "click a date →
 * tiles rendered" duration (founder complaint, map-mv-audit.md §8) rather
 * than re-deriving it elsewhere. Measurement itself lives in
 * `rasterSwitchMetrics.ts` (pure timing, unit-tested independently); this
 * hook only calls `startRasterSwitch`/`finishRasterSwitch` at the same
 * points where it already flips `loading`.
 */
import { useEffect, useRef, useState } from 'react';
import type maplibregl from 'maplibre-gl';
import { isMapAlive } from './isMapAlive';
import { finishRasterSwitch, startRasterSwitch } from './rasterSwitchMetrics';

/**
 * @param map        MapLibre instance (or null before it boots).
 * @param sourceId   Raster source id to observe (e.g. 'ndvi-active-raster').
 * @param loadToken  A value that changes whenever a NEW load starts — the tile
 *                   URL or the selected date. `null` = nothing to load.
 * @returns `true` while tiles for the current token are still loading.
 */
export function useRasterTilesLoading(
  map: maplibregl.Map | null | undefined,
  sourceId: string,
  loadToken: string | null,
): boolean {
  const [loading, setLoading] = useState(false);
  // Mirror of `loading` for use inside event listeners without re-subscribing.
  const loadingRef = useRef(false);

  // Flip to loading synchronously when a fresh token arrives — instant feedback
  // on the user's click, decoupled from the event subscription below.
  useEffect(() => {
    const isLoading = loadToken !== null;
    loadingRef.current = isLoading;
    setLoading(isLoading);
    // WP4 M4 — mark the start of this token's load, keyed by `sourceId` (this
    // hook runs concurrently on /map AND the field-card mini-map — a shared
    // key would let one surface's start overwrite the other's pending entry,
    // see rasterSwitchMetrics.ts module docstring). A token change on the
    // SAME sourceId while a previous one was still pending is a real
    // scenario too (user flips dates fast) — `startRasterSwitch` overwrites
    // that source's pending entry, and the stale token's eventual
    // `finishRasterSwitch` call becomes a no-op, so we never attribute B's
    // tiles to A's clock.
    if (isLoading && loadToken !== null) {
      startRasterSwitch(sourceId, loadToken, performance.now());
    }
  }, [sourceId, loadToken]);

  useEffect(() => {
    if (!isMapAlive(map) || loadToken === null) return;

    const clear = () => {
      if (!loadingRef.current) return;
      loadingRef.current = false;
      setLoading(false);
      // WP4 M4 — `loadToken`/`sourceId` here are the values this effect
      // closed over, i.e. the token that started the load `clear` is now
      // ending (the effect re-runs on every `loadToken`/`sourceId` change,
      // so `clear` always matches its own token, never a newer one).
      finishRasterSwitch(sourceId, loadToken, performance.now());
    };

    const onSourceData = (e: maplibregl.MapSourceDataEvent) => {
      if (e.sourceId !== sourceId) return;
      // `isSourceLoaded` is true once the source's tiles for the current
      // viewport have all settled.
      if (e.isSourceLoaded) clear();
    };

    const onIdle = () => {
      // `idle` = no pending tile fetches / renders for the whole map. A safe
      // backstop when the per-source `sourcedata` signal is missed (e.g. the
      // swap resolved from cache before we subscribed).
      if (!isMapAlive(map)) return;
      clear();
    };

    const onError = (e: maplibregl.ErrorEvent & { sourceId?: string }) => {
      // Only clear on OUR source's error; unrelated errors shouldn't hide the
      // spinner for a load that's still genuinely in flight.
      if (e.sourceId && e.sourceId !== sourceId) return;
      clear();
    };

    map.on('sourcedata', onSourceData);
    map.on('idle', onIdle);
    map.on('error', onError);

    // If the source is already loaded by the time we subscribe (cache hit), the
    // events above may never fire — check once synchronously.
    try {
      const src = map.getSource(sourceId);
      if (src && (src as { loaded?: () => boolean }).loaded?.()) clear();
    } catch {
      // getSource on a torn-down style — isMapAlive guards the common path,
      // but stay defensive.
    }

    return () => {
      if (!isMapAlive(map)) return;
      map.off('sourcedata', onSourceData);
      map.off('idle', onIdle);
      map.off('error', onError);
    };
  }, [map, sourceId, loadToken]);

  return loading;
}
